/**
 * PATH: packages/ui/src/kanban/components/lane/LaneCardItem.tsx
 * FILE_ID: UI-KANBAN-LANE-CARDITEM-001
 * PURPOSE: One rendered card row inside a lane — the indent shell plus the
 *          KanbanChecklistRow / KanbanCard branch. Shared verbatim by
 *          LaneBody's normal flow render and the arc-V7 virtualized window.
 * WHY: The card-branch JSX lived inline in LaneBody's .map(); virtualization
 *      (V7) needs the SAME row under absolute positioning, so the row moved
 *      here rather than being duplicated. Zero behaviour change in flow mode.
 * FLOW: {card, indent, mode} → wrapper div (flow: content-visibility /
 *       virtual: translateY + measureElement) → checklist row | card tile
 * RELATED:
 * - LaneBody.tsx: sole consumer (both branches)
 * - useVirtualizedLaneCards (UI-KANBAN-HOOK-308): supplies virtual props
 */
import type * as React from 'react'

import { CardTileSurface } from '../../card-surface/projections/CardTileSurface'
import { useOptionalCardSurfaceRuntime } from '../../card-surface/runtime/useCardCapabilityRuntime'
import type { CardCommandId } from '../../card-surface/contract'
import { KanbanCard } from '../KanbanCard'
import { KanbanChecklistRow } from '../KanbanChecklistRow'
import { useTouchDragCard } from './useTouchDragCard'
import type { Card } from '../../types'
import type { KanbanLaneProps } from './types'
import type { LaneVM } from './useKanbanLane'

export interface LaneCardItemVirtualProps {
  /** VirtualItem.start — translateY offset inside the sized spacer. */
  start: number
  /** VirtualItem.index — measureElement reads it off `data-index`. */
  index: number
  measureElement: (el: Element | null) => void
}

export function LaneCardItem({
  props,
  vm,
  card: c,
  indent,
  virtual,
}: {
  props: KanbanLaneProps
  vm: LaneVM
  card: Card
  indent: number
  /** Present only when the lane is windowed (arc V7). */
  virtual?: LaneCardItemVirtualProps
}) {
  const { lane, onCardOpen, onCardContextMenu, cardDetailsSlot, resolveMember, currentUserId, onCardRenameInline, cardDensity, cardSurfaceRolloutMode, cardSurfaceId = lane.display_mode === 'checklist' ? 'checklist-row' : 'board-lane-card', keyboardMode, resolveEmailMeta, onCardEmailAllocate, onCardDropAsChild, api, onAiAllocationChanged, notify, onCardChecklistChanged, onCardSetDone, onCardToggleDone, onCardOpenEmail, onCardArchive, highlightQuery, activeFindCardId, selectedCardIds, checklistOpenCardIds, cardQuickActions, relatedCounts, unseenCounts, onCardDrop, boardId, boardKind } = props
  const { handleCardDragStart, handleCardSelect } = vm
  const CardSurface = cardSurfaceRolloutMode === 'v2' ? CardTileSurface : KanbanCard
  const cardSurfaceRuntime = useOptionalCardSurfaceRuntime()
  const executeChecklistCommand = (
    commandId: CardCommandId,
    input: Record<string, unknown> = {},
  ) => {
    if (cardSurfaceRolloutMode !== 'v2') return undefined
    const command = cardSurfaceRuntime?.commands[commandId]
    if (command?.availability !== 'available') return undefined
    return command.execute({ card: c, surfaceId: cardSurfaceId, ...input })
  }
  const hasChecklistCommand = (commandId: CardCommandId) =>
    cardSurfaceRolloutMode === 'v2' &&
    cardSurfaceRuntime?.commands[commandId].availability === 'available'
  const tickHandler =
    hasChecklistCommand('toggle-complete') || onCardToggleDone || onCardSetDone
      ? (card: Card) =>
          executeChecklistCommand('toggle-complete') ??
          (onCardToggleDone ?? onCardSetDone)?.(card)
      : undefined
  const archiveHandler =
    hasChecklistCommand('archive') || onCardArchive
      ? (card: Card) =>
          executeChecklistCommand('archive') ?? onCardArchive?.(card)
      : undefined
  const openHandler =
    hasChecklistCommand('open') || onCardOpen
      ? (card: Card) => {
          const result = executeChecklistCommand('open')
          if (result) void result
          else onCardOpen?.(card)
        }
      : undefined
  const renameHandler =
    hasChecklistCommand('rename') || onCardRenameInline
      ? (card: Card, title: string) =>
          executeChecklistCommand('rename', { title }) ??
          onCardRenameInline?.(card, title)
      : undefined
  const contextMenuHandler =
    hasChecklistCommand('open-context-menu') || onCardContextMenu
      ? (card: Card, anchor: { x: number; y: number }) => {
          const result = executeChecklistCommand('open-context-menu', { anchor })
          if (result) void result
          else onCardContextMenu?.(card, anchor)
        }
      : undefined

  // MOBILE touch drag (UI-KANBAN-LANE-TOUCHDRAG-001) — hold ~250ms then move
  // starts a touch drag; drop routes through the same onCardDrop the HTML5
  // path uses. Attached on this wrapper (touchstart bubbles up from the card
  // root) so KanbanCard's long-press context menu handler stays untouched —
  // its own movement-cancel yields to the drag, and the hook cedes stationary
  // ≥500ms holds back to the menu. Inert on read-only boards (no onCardDrop)
  // and on mouse input (no touch events fire).
  const touchDrag = useTouchDragCard({
    card: c,
    enabled: !!onCardDrop,
    contextMenuWired: !!onCardContextMenu,
  })

  const indentStyle: React.CSSProperties | undefined =
    indent > 0
      ? {
          paddingLeft: `${
            Math.min(indent, 5) * (lane.display_mode === 'checklist' ? 12 : 8)
          }px`,
        }
      : undefined

  // Flow mode keeps UAT P4-b's content-visibility (browser skips layout/
  // paint for off-scrollport cards while every node stays in the DOM).
  // Virtual mode positions the row inside the spacer instead — the window
  // already bounds the DOM, and content-visibility could skip the very
  // layout measureElement needs.
  const style: React.CSSProperties = virtual
    ? {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        transform: `translateY(${virtual.start}px)`,
        // Replaces the flow container's space-y-1.5 stack gap.
        paddingBottom: '6px',
        ...indentStyle,
      }
    : {
        contentVisibility: 'auto',
        containIntrinsicSize:
          lane.display_mode === 'checklist'
            ? 'auto 36px'
            : cardDensity === 'compact'
              ? 'auto 44px'
              : cardDensity === 'large'
                ? 'auto 124px'
                : 'auto 96px',
        ...indentStyle,
      }

  return (
    <div
      data-tree-depth={indent}
      data-index={virtual?.index}
      ref={virtual?.measureElement}
      style={style}
      onTouchStart={touchDrag.onTouchStart}
    >
      {lane.display_mode === 'checklist' ? (
        <KanbanChecklistRow
          card={c}
          // BUG-2026-06-29-KANBAN-CHECKLIST-ONE-WAY-TICK — the tick's
          // own label reads "Mark as not done" once ticked, but it was
          // wired to the ONE-WAY onCardSetDone (always status=DONE) so
          // the operator could never untick. Use the two-way toggle
          // (OPEN <-> DONE) when wired; fall back to one-way set.
          onTickDone={tickHandler}
          onArchive={archiveHandler}
          onOpen={openHandler}
          onRenameInline={renameHandler}
          // 2026-05-22 — parity with the KanbanCard branch:
          // right-click context menu, card-onto-card nest drop,
          // and the dragstart MIME stamp. Without this trio,
          // every checklist-mode lane was silently un-nestable.
          onContextMenu={contextMenuHandler}
          onDropAsChild={onCardDropAsChild}
          onDragStart={handleCardDragStart}
          cardSurfaceId={cardSurfaceId}
          cardSurfaceMode={cardSurfaceRolloutMode === 'v2' ? cardSurfaceRuntime?.mode : undefined}
          api={api}
          currentUserId={currentUserId}
          resolveMember={resolveMember}
        />
      ) : (
        <CardSurface
          card={c}
          cardSurfaceId={cardSurfaceId}
          cardSurfaceMode={cardSurfaceRolloutMode === 'v2' ? cardSurfaceRuntime?.mode : undefined}
          onOpen={onCardOpen}
          onContextMenu={onCardContextMenu}
          selected={selectedCardIds?.has(c.id) ?? false}
          onSelectToggle={handleCardSelect}
          checklistForceOpen={checklistOpenCardIds?.has(c.id) ?? false}
          detailsSlot={cardDetailsSlot}
          resolveMember={resolveMember}
          currentUserId={currentUserId}
          resolveEmailMeta={resolveEmailMeta}
          density={cardDensity}
          keyboardMode={keyboardMode}
          onRenameInline={onCardRenameInline}
          onEmailAllocate={onCardEmailAllocate}
          onDropAsChild={onCardDropAsChild}
          api={api}
          onAiAllocationChanged={onAiAllocationChanged}
          onChecklistChanged={onCardChecklistChanged}
          onToggleComplete={onCardToggleDone}
          onOpenEmail={onCardOpenEmail}
          notify={notify}
          highlightQuery={highlightQuery}
          isActiveFindHit={!!activeFindCardId && c.id === activeFindCardId}
          onDragStart={handleCardDragStart}
          quickActions={cardQuickActions}
          laneKind={lane.kind}
          boardId={boardId}
          boardKind={boardKind}
          relatedCount={relatedCounts?.[c.id] ?? 0}
          unseenCount={unseenCounts?.[c.id] ?? 0}
        />
      )}
    </div>
  )
}
